import { UserRolesEnum } from '@/app/shared/enums/user-roles.enum'
import {
  ExtendedConversationDTO,
  ExtendedMessageDTO,
  ExtendedUserDTO,
  IConversation,
} from '@/app/shared/models/chat/chat.model'
import { PatientInfo } from '@/app/shared/models/patient-info/patient-info.model'
import { HttpClientService } from '@/app/shared/services/api/http-client.service'
import { PermissionService } from '@/app/shared/services/permission.service'
import { inject, Injectable } from '@angular/core'
import { map, Observable, of } from 'rxjs'

export interface HydraCollection<T> {
  member: T[]
  'hydra:member'?: T[]
  totalItems?: number
  'hydra:totalItems'?: number
  view?: {
    '@id': string
    next?: string
    previous?: string
    last?: string
    first?: string
    'hydra:next'?: string
    'hydra:previous'?: string
    'hydra:last'?: string
    'hydra:first'?: string
  }
  'hydra:view'?: {
    '@id': string
    'hydra:next'?: string
    'hydra:previous'?: string
    'hydra:last'?: string
    'hydra:first'?: string
    next?: string
    previous?: string
    last?: string
    first?: string
  }
}

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private httpClient = inject(HttpClientService)
  private permissionService = inject(PermissionService)

  /**
   * Fetch all conversations for a specific user.
   */
  getConversations(
    userId: string | number,
  ): Observable<ExtendedConversationDTO[]> {
    return this.httpClient
      .get<
        HydraCollection<ExtendedConversationDTO>
      >(`api/users/${userId}/conversations`)
      .pipe(map((res) => res.member || res['hydra:member'] || []))
  }

  /**
   * Fetch messages for a specific conversation.
   */
  getMessages(
    convId: number,
    page = 1,
  ): Observable<HydraCollection<ExtendedMessageDTO>> {
    return this.httpClient.get<HydraCollection<ExtendedMessageDTO>>(
      `api/conversations/${convId}/messages?order[createdAt]=desc&page=${page}`,
    )
  }

  /**
   * Mark a conversation as read.
   */
  markAsRead(convId: number): Observable<void> {
    return this.httpClient.patch<void>(
      `api/conversations/${convId}/mark-as-read`,
      {},
    )
  }

  /**
   * Upload a media file.
   */
  uploadMedia(file: File): Observable<{ '@id': string }> {
    const formData = new FormData()
    formData.append('file', file)
    return this.httpClient.post<{ '@id': string }>(`api/media`, formData)
  }

  /**
   * Send a new message.
   */
  sendMessage(
    content: string,
    convId: number | string,
    senderId: string | number,
    recipientId: string | number,
    mediaIri?: string,
  ): Observable<ExtendedMessageDTO> {
    const payload: {
      content: string
      conversation: string
      media?: string
    } = {
      content,
      conversation: `/api/conversations/${convId}`,
    }
    if (mediaIri) {
      payload.media = mediaIri
    }
    return this.httpClient.post<ExtendedMessageDTO>(`api/messages`, payload)
  }

  /**
   * Create a new conversation between a patient and a headox user.
   */
  createConversation(
    patientId: string | number,
    headoxUserId: string | number,
  ): Observable<ExtendedConversationDTO> {
    const payload = {
      patient: `/api/users/${patientId}`,
      headoxUser: `/api/users/${headoxUserId}`,
    }
    return this.httpClient.post<ExtendedConversationDTO>(
      `api/conversations`,
      payload,
    )
  }

  /**
   * Fetch user details by ID or IRI.
   */
  getUser(userIdOrIri: string | number | object): Observable<ExtendedUserDTO> {
    if (typeof userIdOrIri === 'object' && userIdOrIri !== null) {
      return of(userIdOrIri as ExtendedUserDTO)
    }

    const endpoint =
      typeof userIdOrIri === 'string' && userIdOrIri.startsWith('/')
        ? userIdOrIri.substring(1)
        : `api/users/${userIdOrIri}`
    return this.httpClient.get<ExtendedUserDTO>(endpoint)
  }

  /**
   * Fetch message details by IRI.
   */
  getMessage(
    messageIri: string | number | object,
  ): Observable<ExtendedMessageDTO> {
    if (typeof messageIri === 'object' && messageIri !== null) {
      return of(messageIri as ExtendedMessageDTO)
    }

    const s = String(messageIri)
    const endpoint = s.startsWith('/') ? s.substring(1) : s
    return this.httpClient.get<ExtendedMessageDTO>(endpoint)
  }

  /**
   * Patch message details by ID.
   */
  patchMessage(
    messageId: number,
    data: Partial<ExtendedMessageDTO>,
  ): Observable<ExtendedMessageDTO> {
    return this.httpClient.patch<ExtendedMessageDTO>(
      `api/messages/${messageId}`,
      data,
      {
        headers: {
          'Content-Type': 'application/merge-patch+json',
        },
      },
    )
  }
  /**
   * Map DTO to UI model
   */
  mapConversation(
    conv: ExtendedConversationDTO,
    userId: number,
  ): IConversation {
    let name = 'Inconnu'
    let patientId: string | undefined = undefined
    let phone: string | undefined = undefined
    const avatar: string | undefined = conv.avatar

    let otherUser: ExtendedUserDTO | null = null

    // Prioritize patient/headoxUser objects from the new API schema
    if (
      conv.patient &&
      typeof conv.patient === 'object' &&
      Number(conv.patient.id) !== userId
    ) {
      otherUser = conv.patient
    } else if (
      conv.headoxUser &&
      typeof conv.headoxUser === 'object' &&
      Number(conv.headoxUser.id) !== userId
    ) {
      otherUser = conv.headoxUser
    } else {
      // Fallback to participants array
      otherUser =
        conv.participants?.find((p) => Number(p.id) !== userId) || null
    }

    if (otherUser) {
      const fname = otherUser.firstname || ''
      const lname = otherUser.lastname || ''
      name = `${fname} ${lname}`.trim() || 'Inconnu'
      patientId = otherUser.id?.toString()
      phone = otherUser.mobilePhone || undefined
    }

    const lastMsg =
      conv.messages && conv.messages.length > 0
        ? conv.messages[conv.messages.length - 1]
        : null

    let lastMsgText = 'Aucun message'
    // Stability: use createdAt as fallback for updatedAt to avoid jumping with new Date()
    let bestTimestamp = conv.updatedAt
      ? new Date(conv.updatedAt)
      : conv.createdAt
        ? new Date(conv.createdAt)
        : new Date(0)

    if (lastMsg) {
      if (typeof lastMsg === 'object') {
        const lm = lastMsg as ExtendedMessageDTO
        lastMsgText = lm.content || ''
        if (lm.createdAt) {
          bestTimestamp = new Date(lm.createdAt)
        }
      } else {
        lastMsgText = 'Chargement...'
      }
    }

    const isStaff = this.permissionService.hasAnyRole([
      UserRolesEnum.ADMIN,
      UserRolesEnum.ROLE_RT_MANAGER,
      UserRolesEnum.ROLE_AVF_REFERENT,
      UserRolesEnum.ROLE_REGIONAL_MANAGER,
      UserRolesEnum.ROLE_AGENCY_DIRECTOR,
    ])

    return {
      id: conv.id!,
      name: name,
      patientId: patientId,
      avatar: avatar,
      lastMessage: lastMsgText,
      timestamp: bestTimestamp,
      unread: (isStaff ? conv.headoxUnreadCount : conv.patientUnreadCount) || 0,
      patientUnreadCount: conv.patientUnreadCount,
      headoxUnreadCount: conv.headoxUnreadCount,
      function: 'Patient',
      phone: phone,
    }
  }

  /**
   * Map PatientInfo to IConversation
   */
  mapPatientToConversation(patient: PatientInfo): IConversation {
    return {
      id: 0,
      name: `${patient.user.firstname} ${patient.user.lastname}`,
      avatar: '',
      timestamp: new Date(),
      lastMessage: 'Nouvelle conversation',
      unread: 0,
      patientId: patient.user.id.toString(),
      function: 'Patient',
      phone: patient.user.mobilePhone,
    }
  }
}
